import { useState, useEffect } from'react';
import { motion, AnimatePresence } from'framer-motion';

interface Stage {
 id: number;
 name: string;
 hebrew: string;
 description: string;
 color: string;
 duration: number; // seconds
 practice: string;
}

const stages: Stage[] = [
 {
 id: 0,
 name:'Preparation',
 hebrew:'הכנה',
 description:'Centering the mind and preparing for ascent',
 color:'#475569',
 duration: 30,
 practice:'Focus on breath. Clear distracting thoughts. Establish intention.',
 },
 {
 id: 1,
 name:'First Heaven',
 hebrew:'וילון',
 description:'Vilon - The veil that separates day and night',
 color:'#7C3AED',
 duration: 60,
 practice:'Visualize passing through a thin curtain of light.',
 },
 {
 id: 2,
 name:'Second Heaven',
 hebrew:'רקיע',
 description:'Rakia - Where sun, moon, and stars are fixed',
 color:'#06B6D4',
 duration: 60,
 practice:'Contemplate the celestial lights and cosmic order.',
 },
 {
 id: 3,
 name:'Third Heaven',
 hebrew:'שחקים',
 description:'Shechakim - The realm of manna and sustenance',
 color:'#10B981',
 duration: 60,
 practice:'Meditate on divine provision and spiritual nourishment.',
 },
 {
 id: 4,
 name:'Fourth Heaven',
 hebrew:'זבול',
 description:'Zebul - The heavenly Jerusalem and Temple',
 color:'#FBBF24',
 duration: 60,
 practice:'Envision the celestial sanctuary.',
 },
 {
 id: 5,
 name:'Fifth Heaven',
 hebrew:'מעון',
 description:'Maon - Where angelic choirs sing praises',
 color:'#F59E0B',
 duration: 60,
 practice:'Listen inwardly to the sound of sacred hymns.',
 },
 {
 id: 6,
 name:'Sixth Heaven',
 hebrew:'מכון',
 description:'Machon - Storehouses of snow, hail, and blessings',
 color:'#EC4899',
 duration: 60,
 practice:'Contemplate the hidden powers and potentials.',
 },
 {
 id: 7,
 name:'Seventh Heaven',
 hebrew:'ערבות',
 description:'Aravot - The throne of glory and divine presence',
 color:'#EA580C',
 duration: 90,
 practice:'Rest in silent awareness before the throne.',
 },
];

export function MeditationTracker({ className ='' }: { className?: string }) {
 const [currentStage, setCurrentStage] = useState(0);
 const [isActive, setIsActive] = useState(false);
 const [timeRemaining, setTimeRemaining] = useState(stages[0].duration);
 const [completedStages, setCompletedStages] = useState<number[]>([]);

 useEffect(() => {
 setTimeRemaining(stages[currentStage].duration);
 }, [currentStage]);

 useEffect(() => {
 if (!isActive || timeRemaining <= 0) return;

 const timer = setInterval(() => {
 setTimeRemaining((prev) => {
 if (prev <= 1) {
 handleStageComplete();
 return 0;
 }
 return prev - 1;
 });
 }, 1000);

 return () => clearInterval(timer);
 }, [isActive, timeRemaining]);

 const handleStageComplete = () => {
 setCompletedStages((prev) => [...prev, currentStage]);
 setIsActive(false);

 if (currentStage < stages.length - 1) {
 // Auto-advance to next stage after a short delay
 setTimeout(() => {
 setCurrentStage(currentStage + 1);
 }, 1000);
 }
 };

 const handleStart = () => {
 setIsActive(true);
 };

 const handlePause = () => {
 setIsActive(false);
 };

 const handleReset = () => {
 setIsActive(false);
 setCurrentStage(0);
 setTimeRemaining(stages[0].duration);
 setCompletedStages([]);
 };

 const handleSkip = () => {
 if (currentStage < stages.length - 1) {
 setCompletedStages((prev) => [...prev, currentStage]);
 setCurrentStage(currentStage + 1);
 setIsActive(false);
 }
 };

 const progress = ((stages[currentStage].duration - timeRemaining) / stages[currentStage].duration) * 100;
 const totalProgress = ((currentStage + progress / 100) / stages.length) * 100;

 const formatTime = (seconds: number): string => {
 const mins = Math.floor(seconds / 60);
 const secs = seconds % 60;
 return `${mins}:${secs.toString().padStart(2,'0')}`;
 };

 return (
 <div className={`relative ${className}`}>
 <div className="mb-6 text-center">
 <h3 className="text-2xl font-bold text-white mb-2">Heavenly Ascent Meditation</h3>
 <p className="text-sm text-slate-400">
 Journey through the seven heavens — {stages.length} stages
 </p>
 </div>

 {/* Current Stage Display */}
 <div className="relative w-full max-w-2xl mx-auto mb-6">
 <div
 className="p-8 rounded-2xl border-2 transition-all duration-500"
 style={{
 backgroundColor: `${stages[currentStage].color}15`,
 borderColor: stages[currentStage].color,
 boxShadow: isActive ? `0 0 40px ${stages[currentStage].color}80` :'none',
 }}
 >
 {/* Stage Info */}
 <div className="text-center mb-6">
 <motion.div
 key={currentStage}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="mb-4"
 >
 <div
 className="inline-block px-4 py-1 rounded-full text-xs font-semibold mb-3"
 style={{
 backgroundColor: `${stages[currentStage].color}30`,
 color: stages[currentStage].color,
 }}
 >
 Stage {currentStage + 1} of {stages.length}
 </div>
 <h4 className="text-3xl font-bold text-white mb-2">
 {stages[currentStage].name}
 </h4>
 <p
 className="hebrew-text text-2xl mb-3"
 style={{ color: stages[currentStage].color }}
 >
 {stages[currentStage].hebrew}
 </p>
 <p className="text-slate-300 mb-4">{stages[currentStage].description}</p>
 </motion.div>

 {/* Timer Display */}
 <div className="mb-6">
 <div
 className="text-6xl font-bold mb-2"
 style={{ color: stages[currentStage].color }}
 >
 {formatTime(timeRemaining)}
 </div>
 <div className="w-full bg-slate-700 h-2 rounded-full overflow-hidden">
 <motion.div
 className="h-full"
 style={{ backgroundColor: stages[currentStage].color }}
 initial={{ width: 0 }}
 animate={{ width: `${progress}%` }}
 transition={{ duration: 0.5 }}
 />
 </div>
 </div>

 {/* Practice Instructions */}
 <div
 className="p-4 rounded-lg text-left"
 style={{ backgroundColor: `${stages[currentStage].color}20` }}
 >
 <div className="text-xs text-slate-400 mb-1">Practice:</div>
 <p className="text-sm text-slate-200">{stages[currentStage].practice}</p>
 </div>
 </div>

 {/* Controls */}
 <div className="flex items-center justify-center gap-3">
 {!isActive ? (
 <button
 onClick={handleStart}
 className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
 >
 <span>{currentStage === 0 && completedStages.length === 0 ?'Begin Journey' :'Continue'}</span>
 <span>▶</span>
 </button>
 ) : (
 <button
 onClick={handlePause}
 className="px-6 py-3 bg-amber-600 hover:bg-[#3366cc] text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
 >
 <span>Pause</span>
 <span>⏸</span>
 </button>
 )}

 {currentStage < stages.length - 1 && (
 <button
 onClick={handleSkip}
 className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-all duration-200"
 >
 Skip →
 </button>
 )}

 <button
 onClick={handleReset}
 className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-all duration-200"
 >
 Reset
 </button>
 </div>
 </div>
 </div>

 {/* Overall Progress */}
 <div className="w-full max-w-2xl mx-auto mb-6">
 <div className="text-xs text-slate-400 mb-2 text-center">
 Overall Progress: {Math.round(totalProgress)}%
 </div>
 <div className="w-full bg-slate-800 h-3 rounded-full overflow-hidden">
 <motion.div
 className="h-full bg-gradient-to-r from-violet-600 via-orange-500 to-amber-400"
 animate={{ width: `${totalProgress}%` }}
 transition={{ duration: 0.5 }}
 />
 </div>
 </div>

 {/* Stage Progress Indicators */}
 <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 sm:grid-cols-8 gap-2 max-w-2xl mx-auto">
 {stages.map((stage) => {
 const isCompleted = completedStages.includes(stage.id);
 const isCurrent = currentStage === stage.id;
 const isUpcoming = stage.id > currentStage;

 return (
 <button
 key={stage.id}
 onClick={() => {
 if (stage.id <= currentStage) {
 setCurrentStage(stage.id);
 setIsActive(false);
 }
 }}
 disabled={isUpcoming}
 className={`p-2 rounded-lg border transition-all duration-200 ${
 isUpcoming ?'opacity-40 cursor-not-allowed' :'hover:scale-105 cursor-pointer'
 }`}
 style={{
 backgroundColor: isCurrent ? `${stage.color}30` : isCompleted ? `${stage.color}20` : `${stage.color}10`,
 borderColor: isCurrent ? stage.color : isCompleted ? `${stage.color}60` : `${stage.color}30`,
 boxShadow: isCurrent ? `0 0 15px ${stage.color}60` :'none',
 }}
 >
 <div className="text-center">
 <div className="text-xs font-bold mb-1" style={{ color: stage.color }}>
 {isCompleted ?'✓' : stage.id + 1}
 </div>
 <div className="hebrew-text text-[9px]" style={{ color: stage.color }}>
 {stage.hebrew}
 </div>
 </div>
 </button>
 );
 })}
 </div>

 {/* Completion Message */}
 <AnimatePresence>
 {currentStage === stages.length - 1 && timeRemaining === 0 && (
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 exit={{ opacity: 0, scale: 0.9 }}
 className="mt-6 p-6 rounded-xl bg-gradient-to-br from-orange-500/20 to-violet-600/20 border-2 border-orange-400 text-center"
 >
 <div className="text-4xl mb-3">✨</div>
 <h4 className="text-2xl font-bold text-white mb-2">Journey Complete</h4>
 <p className="text-slate-300 mb-4">
 You have ascended through all seven heavens and beheld the throne of glory
 </p>
 <p className="hebrew-text text-lg text-orange-400">
 ברוך השם
 </p>
 <button
 onClick={handleReset}
 className="mt-4 px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium transition-all duration-200"
 >
 Begin Again
 </button>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 );
}
